
import React, { useState, useEffect } from 'react';
import { format, parseISO, addDays, startOfWeek, isSameDay } from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import ClassDetailsDialog from './ClassDetailsDialog';
import { toast } from 'sonner';
import { useTimetableData } from '../../hooks/useTimetableData';

const TimetableCalendar = ({ timetableData = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedClass, setSelectedClass] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [localTimetableData, setLocalTimetableData] = useState([]);
  const { refreshData, isRefreshing } = useTimetableData();
  const [isRefreshingLocal, setIsRefreshingLocal] = useState(false);
  
  useEffect(() => {
    console.log('TimetableCalendar received new data:', timetableData);
    if (timetableData && Array.isArray(timetableData)) {
      setLocalTimetableData(timetableData);
      console.log('Local timetable data updated with', timetableData.length, 'teachers');
    }
  }, [timetableData]);
  
  const goToPreviousWeek = () => {
    setCurrentDate(prevDate => addDays(prevDate, -7));
  };
  
  const goToNextWeek = () => {
    setCurrentDate(prevDate => addDays(prevDate, 7));
  };
  
  const handleRefresh = async () => {
    if (isRefreshing || isRefreshingLocal) return;
    
    setIsRefreshingLocal(true);
    
    try {
      console.log('Manual refresh triggered');
      const success = await refreshData();
      if (success) {
        toast.success('Timetable data updated');
      }
    } catch (error) {
      console.error('Failed to update timetable data:', error);
      toast.error('Failed to update timetable data');
    } finally {
      setIsRefreshingLocal(false);
    }
  };
  
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));
  
  const getClassesForDay = (day) => {
    if (!localTimetableData || !Array.isArray(localTimetableData) || localTimetableData.length === 0) {
      return [];
    }
    
    const classes = [];
    
    localTimetableData.forEach(teacher => {
      if (!teacher.classes || !Array.isArray(teacher.classes)) {
        return;
      }
      
      teacher.classes.forEach(classItem => {
        if (!classItem || !classItem.startTime) {
          return;
        }
        
        try {
          const classDate = parseISO(classItem.startTime);
          if (isSameDay(classDate, day)) {
            classes.push({
              ...classItem,
              teacherName: classItem.teacherName || teacher.name,
              teacherId: classItem.teacherId || teacher.id
            });
          }
        } catch (error) {
          console.error('Error parsing date for class:', classItem.startTime, error);
        }
      });
    });
    
    return classes.sort((a, b) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
  };
  
  const openClassDetails = async (classItem) => {
    setSelectedClass(classItem);
    setIsDialogOpen(true);
  };

  const handleDialogClose = async () => {
    setIsDialogOpen(false);
    setSelectedClass(null);
    
    // Refresh data when dialog closes to ensure any updates are reflected
    try {
      await refreshData();
    } catch (error) {
      console.error('Error refreshing data after dialog close:', error);
    }
  };

  const getStatusText = (status) => {
    if (typeof status === 'object' && status !== null) {
      return typeof status.status === 'string' 
        ? status.status.toUpperCase() 
        : String(status.status || '');
    }
    return typeof status === 'string' 
      ? status.toUpperCase() 
      : String(status || '');
  };
  
  const getStatusClass = (status) => {
    const statusText = getStatusText(status);
    if (statusText === 'ABSENT') return 'bg-red-100 text-red-800 border-red-500';
    if (statusText === 'LATE') return 'bg-yellow-100 text-yellow-800 border-yellow-500';
    return 'bg-green-100 text-green-800 border-green-500';
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const totalClasses = localTimetableData.reduce((total, teacher) => total + (teacher.classes?.length || 0), 0);
  
  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xl flex items-center">
          <CalendarIcon className="mr-2 h-5 w-5" />
          Weekly Schedule
          {totalClasses > 0 && (
            <span className="ml-2 text-sm text-green-600">
              ({totalClasses} classes from {localTimetableData.length} sources)
            </span>
          )}
          {totalClasses === 0 && (
            <span className="ml-2 text-sm text-gray-500">
              (No classes scheduled)
            </span>
          )}
        </CardTitle>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={goToPreviousWeek}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-medium">
            {format(weekStart, "MMM d")} - {format(addDays(weekStart, 6), "MMM d, yyyy")}
          </span>
          <Button variant="outline" size="icon" onClick={goToNextWeek}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRefresh} 
            disabled={isRefreshing || isRefreshingLocal}
            title="Refresh timetable data"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing || isRefreshingLocal ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((day, idx) => {
            const isToday = isSameDay(day, today);
            const dayClasses = getClassesForDay(day);
            return (
              <div 
                key={idx} 
                className={`border rounded min-h-[150px] p-1 
                  ${isToday ? 'border-primary border-2' : 'border-gray-200'}`}
              >
                <div className={`text-center py-1 font-medium text-sm border-b mb-1 
                  ${isToday ? 'text-primary' : ''}`}>
                  {format(day, "EEE")}
                  <div className={`text-xs ${isToday ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                    {format(day, "MMM d")}
                  </div>
                </div>
                <div className="space-y-1 max-h-[250px] overflow-auto">
                  {dayClasses.map((classItem, classIdx) => (
                    <div 
                      key={`${classItem.id}-${classIdx}`}
                      className={`text-xs p-1 rounded cursor-pointer transition-colors hover:bg-muted ${
                        getStatusClass(classItem.status)
                      }`}
                      onClick={() => openClassDetails(classItem)}
                    >
                      <div className="font-medium truncate">{classItem.subject}</div>
                      <div className="text-[10px]">{format(parseISO(classItem.startTime), "h:mm a")}</div>
                      <div className="text-[10px] font-semibold truncate">
                        👨‍🏫 {classItem.teacherName && classItem.teacherName !== 'Unknown Teacher' 
                          ? classItem.teacherName 
                          : "Unassigned"}
                      </div>
                      <div className="text-[10px] truncate">Room: {classItem.room}</div>
                      {classItem.status && getStatusText(classItem.status) !== 'PRESENT' && (
                        <Badge 
                          variant="outline" 
                          className={`mt-1 text-[9px] ${
                            getStatusText(classItem.status) === 'ABSENT' ? 'border-red-500 text-red-800' : 
                            'border-orange-500 text-orange-800'
                          }`}
                        >
                          {getStatusText(classItem.status)}
                        </Badge>
                      )}
                    </div>
                  ))}
                  {dayClasses.length === 0 && (
                    <div className="text-xs text-center py-2 text-muted-foreground">
                      No classes
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
      
      {selectedClass && (
        <ClassDetailsDialog 
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          classData={selectedClass}
        />
      )}
    </Card>
  );
};

export default TimetableCalendar;
